import { DateTime } from "luxon";

import { UserLifeCycle } from "@/types/user.types";

export const getDateDiffInHours = (startTime: DateTime, endTime: DateTime) => {
    if (!startTime || !endTime) return "";

    const diff = endTime.diff(startTime, ["hours", "minutes"]);

    return `${Math.floor(diff.hours)}h ${Math.round(diff.minutes)}m`;
};

export function formatTime(time?: string) {
    return time || "--:--";
}

export const dayAgo = (time: number, locale: string) => {
    const date = DateTime.fromSeconds(time);
    return date.isValid ? date.setLocale(locale).toRelative() : "";
};

export function formatDate(dateStr?: string): string {
    if (!dateStr) return "";
    try {
        const date = DateTime.fromISO(dateStr);
        return date.isValid ? date.toFormat("dd.MM.yyyy") : dateStr;
    } catch {
        return dateStr; // Fallback to original string
    }
}

/**
 * Calculates the number of nights between arrival and departure dates
 * @param {string | undefined} arrivalDate - The arrival date in ISO format
 * @param {string | undefined} departureDate - The departure date in ISO format
 * @returns {number} Number of nights between dates, or 0 if calculation fails
 *
 * @example
 * ```tsx
 * const nights = calculateNights("2024-03-17", "2024-03-19");
 * // returns 2
 * ```
 */
export function calculateNights(arrivalDate?: string, departureDate?: string): number {
    if (!arrivalDate || !departureDate) return 0;

    try {
        const arrival = DateTime.fromISO(arrivalDate);
        const departure = DateTime.fromISO(departureDate);

        if (!arrival.isValid || !departure.isValid) return 0;

        // Calculate the difference in days
        const diff = departure.diff(arrival, "days");
        return Math.max(0, Math.round(diff.days));
    } catch (error) {
        console.error("Error calculating nights:", error);
        return 0;
    }
}

export const getTotalWorkDuration = (lifecycles: UserLifeCycle[]) => {
    if (!lifecycles || lifecycles.length === 0) {
        return "0 ay 0 gün";
    }

    let totalMonths = 0;
    let totalDays = 0;

    lifecycles.forEach((cycle) => {
        if (!cycle.startedAt) return;

        const startTime = DateTime.fromISO(cycle.startedAt);
        const endTime = cycle.leavedAt ? DateTime.fromISO(cycle.leavedAt) : DateTime.now();

        const diff = endTime.diff(startTime, ["months", "days"]);

        totalMonths += Math.floor(diff.months);
        totalDays += Math.round(diff.days);
    });

    const extraMonths = Math.floor(totalDays / 30);
    totalMonths += extraMonths;
    totalDays = totalDays % 30;

    return `${totalMonths} ay ${totalDays} gün`;
};
